import { array, nonEmptyArray, record, separated } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { head } from 'lodash/fp';
import { useEffect } from 'react';
import { OrderItemType } from '@mews-commander/data-access';
import {
    isProductOrderItemWithAgeCategory,
    OrderItem,
    ProductOrderItemWithAgeCategory,
    WithScope,
    WithServiceId,
} from '@mews-commander/types';
import { withScopeContext } from '@mews/framework';
import { useCommanderSelector } from '@mews-commander/utils';
import { ExpandableBillListItem } from '../../../../components';
import { TotalAmountAndCount } from '../../../../types';
import { OrderItemListItem } from '../OrderItemListItem';
import { OrderItemProductLabel } from '../OrderItemProductLabel';
import { isAnyOrderItemExpanded as isAnyOrderItemHighlighted } from '../../../../ducks/billing';
import { useServiceOrderItemsFlags } from '../useServiceOrderItemsFlags';
import { toggleOrderItem, useIsOrderItemExpanded } from '../store';
import { SelectState } from '../types';
import { connectOrderItemProductList } from './connectOrderItemProductList';

interface OrderItemProductListProps extends WithServiceId, WithScope {
    groupId: string;
    ageCategoryId?: string;
    orderItems: Array<OrderItem>;
    serviceOrderId: string;
    billId: string;
    month: string;
    selectState: SelectState;
    toggleSelect(): void;
    type: OrderItemType;
    value: string;
    totalAmountAndCount: TotalAmountAndCount;
    allItemsHaveSameNotes: boolean;
    areAllOrderItemsRebated: boolean;
    areSomeOrderItemsRebated: boolean;
    areAllOrderItemsRebates: boolean;
    areAllOrderItemsClosed: boolean;
    collectiveRebateIndex?: number;
    depth?: number;
}

export const OrderItemProductList: React.FC<OrderItemProductListProps> = ({
    groupId,
    ageCategoryId,
    orderItems,
    selectState,
    toggleSelect,
    serviceOrderId,
    totalAmountAndCount,
    type,
    allItemsHaveSameNotes,
    areAllOrderItemsRebated,
    areSomeOrderItemsRebated,
    areAllOrderItemsRebates,
    areAllOrderItemsClosed,
    collectiveRebateIndex,
    serviceId,
    billId,
    scope,
    month,
    depth,
    ...other
}) => {
    const areAnyOrderItemsExpanded = useCommanderSelector((state) =>
        isAnyOrderItemHighlighted(state, { orderItemIds: orderItems.map((o) => o.id), scope })
    );

    const orderItemKey = `${billId}-${groupId}-${type}-${ageCategoryId}`;

    useEffect(() => {
        if (areAnyOrderItemsExpanded) toggleOrderItem(serviceOrderId, orderItemKey, true);
    }, [areAnyOrderItemsExpanded, orderItemKey, serviceOrderId]);

    const isExpanded = useIsOrderItemExpanded(serviceOrderId, orderItemKey);
    const { areAllItemsReinstated } = useServiceOrderItemsFlags({ billId, month, serviceId, serviceOrderId });

    const firstOrderItem = head(orderItems);
    const primaryText = firstOrderItem ? (
        <OrderItemProductLabel
            serviceOrderId={serviceOrderId}
            type={type}
            orderItemId={firstOrderItem.id}
            allSelected={selectState === 'ALL'}
            someSelected={selectState === 'SOME' || selectState === 'ALL'}
            toggleSelect={toggleSelect}
            totalAmountAndCount={totalAmountAndCount}
            showNotes={allItemsHaveSameNotes}
            isRebated={areSomeOrderItemsRebated}
            rebatedTagType={areAllOrderItemsRebated ? 'fully' : 'partially'}
            isRebate={areAllOrderItemsRebates && !areAllItemsReinstated}
            isClosed={areAllOrderItemsClosed}
            rebateIndex={collectiveRebateIndex}
            showAgeCategory={depth !== 1}
        />
    ) : null;

    const renderOrderItemListItems = array.map((orderItem: OrderItem) => (
        <OrderItemListItem
            key={orderItem.id}
            orderItem={orderItem}
            orderItemId={orderItem.id}
            serviceId={serviceId}
            showNotes={!allItemsHaveSameNotes}
        />
    ));

    const renderOrderItemListItemsWithAgeCategory = flow(
        nonEmptyArray.groupBy(({ data }: ProductOrderItemWithAgeCategory) => data.value.ageCategoryId),
        record.toArray,
        array.map(([key]) => (
            <ConnectedOrderItemProductList
                type={type}
                groupId={groupId}
                serviceOrderId={serviceOrderId}
                serviceId={serviceId}
                billId={billId}
                key={key}
                value={key}
                month={month}
                ageCategoryId={key}
            />
        ))
    );

    return (
        <ExpandableBillListItem
            data-order-item-product-list={serviceOrderId}
            primaryText={primaryText}
            depth={depth}
            {...other}
            opened={isExpanded}
            onClick={() => toggleOrderItem(serviceOrderId, orderItemKey, !isExpanded)}
            multiSelect
        >
            {pipe(
                orderItems,
                array.partition(
                    (orderItem): orderItem is ProductOrderItemWithAgeCategory =>
                        !ageCategoryId && isProductOrderItemWithAgeCategory(orderItem)
                ),
                separated.bimap(renderOrderItemListItems, renderOrderItemListItemsWithAgeCategory),
                ({ left, right }) => [...right, ...left]
            )}
        </ExpandableBillListItem>
    );
};

export const ConnectedOrderItemProductList = pipe(OrderItemProductList, connectOrderItemProductList, withScopeContext);
